import {AdditionalInformationSectionConsts} from '../../../../../shared/sections/web/additional-information-section/additional-information-section.consts';
import {RegistrationService} from './../../registration.service';
import {Injectable} from '@angular/core';
import {ConfigurationService} from '../../configuration/configuration.service';
import {Dictionaries, UiConfig} from '../../../../configuration/ui-config';
import {
  AdditionalInformationModeConfig,
  VisibilityRequirement
} from '../../../../../shared/sections/web/additional-information-section/additional-information-mode.config';
import {AdditionalInformationSectionModeConfig} from '../../../../../shared/sections/web/additional-information-section/additional-information-section-mode.config';
import {ApplicationStateService} from '../../../../../shared/services/application-state.service';
import {SelectedOfferService} from '../selected-offer/selected-offer.service';
import {VehicleQuestionCode} from '../../../../consts/vehicle-question-code.enum';
import {CalculationService} from '../../calculation.service';
import {Dictionary} from '../../../../configuration/dictionary';
import {VehicleRegistrationData} from '../../../../models/registration-offer-data/vehicle-registration-data.model';
import {RegistrationStorageService} from '../../../../../shared/services/registration-storage.service';
import {VehicleQuestionsService} from '../../../vehicle-questions.service';
import {VehicleModelStateService} from '../vehicle-model/vehicle-model-state.service';
import {addDaysToDate, changeDateByYears} from '../../../../utils/utils';
import {InsuranceDataStateService} from '../vehicle-data/insurance-data-state.service';
import * as moment from 'moment';
import {SessionService} from '../../session/session.service';
import {CalculationData} from '../../../../models/calculation-data.model';

@Injectable({
  providedIn: 'root'
})
export class AdditionalInformationStateService {

  modeConfig: AdditionalInformationModeConfig = null;
  public consts = AdditionalInformationSectionConsts;
  questionFields = [
    this.consts.KEYS_AMOUNT,
    this.consts.ORIGINAL_KEYS_AMOUNT,
    this.consts.LEECHED_KEYS_AMOUNT,
    this.consts.IMMOBILIZER,
    this.consts.AUTOALARM,
    this.consts.GPS,
    this.consts.MECHANIC_LOCK_TRANSMISSION,
    this.consts.USA_CANADA_REGISTRATION,
    this.consts.OTHER_SECURITIES
  ];
  nonQuestionFields = [this.consts.VIN, this.consts.TECHNICAL_INSPECTION_DATE];
  private dictionaries: Dictionaries;
  private fields = [...this.nonQuestionFields, ...this.questionFields];

  constructor(private configuration: ConfigurationService,
              private registration: RegistrationService,
              private calculation: CalculationService,
              private storage: RegistrationStorageService,
              private stateService: ApplicationStateService,
              private selectedOfferService: SelectedOfferService,
              private vehicleModelStateService: VehicleModelStateService,
              private insuranceDataStateService: InsuranceDataStateService,
              private vehicleQuestionsService: VehicleQuestionsService,
              private sessionService: SessionService) {
    this.dictionaries = this.configuration.uiConfig.dictionaries;
  }

  get vehicleData(): VehicleRegistrationData {
    return this.registration.vehicleData;
  }

  get keysAmountDictionary(): Dictionary {
    if (this.dictionaries) {
      return this.dictionaries[UiConfig.KEYS_AMOUNT];
    }
    return null;
  }

  get leechedKeysAmount(): Dictionary {
    if (this.dictionaries) {
      return this.dictionaries[UiConfig.LEECHED_KEYS_AMOUNT];
    }
    return null;
  }

  get isRegistrationNumberUnknown(): boolean {
    return this.vehicleModelStateService.isRegistrationNumberUnknown;
  }

  isVisible(key: string): boolean {
    if (this.modeConfig[key]) {
      return this.modeConfig[key].visible;
    }
    return false;
  }

  hasDefaultValue(key: string): boolean {
    return this.modeConfig[key] && this.modeConfig[key].defaultValue;
  }

  isQuestionField(key: string): boolean {
    return this.questionFields.includes(key);
  }

  initModeConfig(): void {
    const modeConfigBuilder = new AdditionalInformationSectionModeConfig(this.stateService.insuranceCompanyCode);
    const modeConfig = modeConfigBuilder.getConfig();
    this.modeConfig = this.resolveVisibilityRequirements(modeConfig);
    this.updateVehicleData();
  }

  changeVinNumber(vinNumber: string) {
    this.vehicleData.vin = vinNumber;
    this.storage.updateRegistration();
  }

  changeTechnicalInspectionDate(date: Date): void {
    this.vehicleData.technicalInspectionDate = date;
    this.storage.updateRegistration();
  }

  changeQuestionAnswer(fieldKey: string, value: any) {
    this.vehicleQuestionsService.updateQuestion(this.vehicleData, fieldKey, value);
    this.storage.updateRegistration();
  }

  setInitialValues() {
    if (this.stateService.firstEntry) { // set initial values only once to not overwrite values defined by user
      this.fields.forEach(key => this.updateValueFromModeConfig(key));
    }
  }

  createVehicleInfoString(): string {
    return `${this.vehicleModelStateService.vehicleData.brandName}, ${this.vehicleModelStateService.vehicleData.modelName}`;
  }

  createInsurancePeriodString(): string {
    const startDate = this.insuranceDataStateService.insuranceData.startInsuranceDate;
    const endDate = addDaysToDate(changeDateByYears(new Date(startDate), 1), -1);
    const startDateFormatted = moment(startDate).format('DD.MM.YYYY');
    const endDateFormatted = moment(endDate).format('DD.MM.YYYY');
    return startDate ? `${startDateFormatted} - ${endDateFormatted}` : '';
  }

  createRegistrationNumberString(): string {
    return this.vehicleModelStateService.vehicleData.registrationNumber;
  }

  async getVinFromSession(): Promise<string> {
    return new Promise(res => {
      this.sessionService.getSessionDetails()
          .then(resp => {
            try {
              const calculationData: CalculationData = resp.values.body.calculationData;
              if (calculationData && calculationData.vehicle) {
                res(calculationData.vehicle.vin);
              }
            } catch (err) {
              res(null);
            }
          })
          .catch(err => res(null));
    })
  }

  private resolveVisibilityRequirements(config: AdditionalInformationModeConfig): AdditionalInformationModeConfig {
    Object.keys(config).forEach(fieldKey => {
      const fieldConfig = config[fieldKey];
      const allowedToBeVisible = this.checkGlobalVisibilityRequirement(fieldKey); // checks visibility requirements independent from insurance company
      if (allowedToBeVisible) { // global visibility requirement is fulfilled - check insurance company requirements
        if (fieldConfig.visibilityRequirements && fieldConfig.visible) {
          const allRequirementsFulfilled: boolean = fieldConfig.visibilityRequirements
            .map(req => this.isRequirementFulFilled(req)) // map to array of booleans
            .every(reqTestResult => reqTestResult); // true if every test result is true
          fieldConfig.visible = allRequirementsFulfilled; // update visibility of field after resolving requirements
        }
      } else {
        fieldConfig.visible = false;
      }
    });
    return config;
  }

  private checkGlobalVisibilityRequirement(fieldKey: string): boolean {
    switch (fieldKey) {
      case this.consts.USA_CANADA_REGISTRATION:
        return this.calculation.isVehicleImported;
      default:
        return true;
    }
  }

  private isRequirementFulFilled(requirement: VisibilityRequirement): boolean {
    switch (requirement) {
      case VisibilityRequirement.HAS_AC:
        return this.selectedOfferService.hasAC();
      case VisibilityRequirement.HAS_AC_OR_AC_MINI:
        return this.selectedOfferService.hasACOrACMini();
      default:
        console.error('Unknown visibility requirement:', requirement);
    }
  }

  /**
   * method clears values in vehicle data of fields that are not visible in current section mode config
   */
  private updateVehicleData(): void {
    this.fields.forEach(key => {
      if (!this.isVisible(key)) {
        this.removeValueFromModel(key);
      }
    })
  }

  private removeValueFromModel(key: string): void {
    if (this.isQuestionField(key)) {
      this.vehicleData.questions = this.vehicleData.questions.filter(question => question.questionCode !== VehicleQuestionCode[key]);
    } else {
      delete this.vehicleData[key];
    }
  }

  private updateValueFromModeConfig(key: string): void {
    if (this.isVisible(key) && this.hasDefaultValue(key)) {
      const value = this.modeConfig[key].defaultValue;
      if (this.isQuestionField(key)) {
        this.changeQuestionAnswer(key, value);
      } else {
        this.vehicleData[key] = value;
      }
    }
  }
}
